import React from 'react';
import PropTypes from 'prop-types';
import assign from 'lodash/assign';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import omit from 'lodash/omit';
import partial from 'lodash/partial';
import reduce from 'lodash/reduce';
import reject from 'lodash/reject';
import sortBy from 'lodash/sortBy';
import values from 'lodash/values';
import concat from 'lodash/concat';
import includes from 'lodash/includes';
import upperFirst from 'lodash/upperFirst';

import Button from 'zignal-landscape/lib/Button';
import ButtonGroup from 'zignal-landscape/lib/ButtonGroup';
import Link from 'zignal-landscape/lib/Link';
import Modal from 'zignal-landscape/lib/Modal';
import SearchInput from 'zignal-landscape/lib/SearchInput';
import ModalHeader from 'zignal-landscape/lib/ModalHeader';
import Col from 'zignal-landscape/lib/Col';

import { getWidgetKey } from './helpers.js';

import CategoryList from './CategoryList.js';
import CustomWidgetCategoryView from './CustomWidgetCategory/CustomWidgetCategoryView.js';
import CategoryView from './CategoryView.js';
import CategoryItem from './CategoryItem.js';
import Drawer from './Drawer.js';

import styles from './AddWidgetModal.css';
import colors from 'zignal-landscape/lib/styles/colors.css';

const gray01 = colors.gray01;

const makeWrapperComponent = (styles, className, TagName = 'div') => {  // eslint-disable-line no-shadow
  // JSX compiler requires `TagName` variable to be capitalized (can't use `tagName`)
  /* eslint-disable */
  const Wrapper = ({children, ...other}) => <TagName {...other} className={styles[className]}>{children}</TagName>;
  /* eslint-enable */

  Wrapper.displayName = upperFirst(className);
  Wrapper.propTypes = {
    children: PropTypes.node
  };

  return Wrapper;
};

const makeStyledWrapper = partial(makeWrapperComponent, styles);

const ModalBody = makeStyledWrapper('modalBody');
const CategoryScrollView = makeStyledWrapper('categoryScrollView');
const Footer = makeStyledWrapper('footer', 'footer');
const EmptySearchResultsView = makeStyledWrapper('emptySearchResultsView');

class AddWidgetModal extends React.Component {
  constructor(props) {
    super(props);

    this.decrement = this.decrement.bind(this);
    this.increment = this.increment.bind(this);

    this.handleCancel = this.handleCancel.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleSuccess = this.handleSuccess.bind(this);

    this.addNewWidget = this.addNewWidget.bind(this);
    this.deleteCustomWidget = this.deleteCustomWidget.bind(this);
    this.editWidget = this.editWidget.bind(this);

    this.setSelectedCategory = this.setSelectedCategory.bind(this);
    this.onSearchInputChange = this.onSearchInputChange.bind(this);
    this.toggleDrawer = this.toggleDrawer.bind(this);
    this.onSearchInputBlur = this.onSearchInputBlur.bind(this);
    this.onSearchInputKeyDown = this.onSearchInputKeyDown.bind(this);
    this.focusSearchInput = this.focusSearchInput.bind(this);

    this.state = this.createInitialState();
  }

  componentDidUpdate(prevProps) {
    const { isOpen: prevIsOpen } = prevProps;
    const { isOpen } = this.props;
    if (isOpen === true && prevIsOpen === false) {
      this.focusSearchInput();
    }
  }

  onSearchInputChange(text) {
    const selectedCategory = text.length > 0 ? 'Search Results' : 'Popular';
    this.setState({searchText: text.trim(), selectedCategory});
  }

  onSearchInputKeyDown(e) {
    switch (e.key) {
      case 'ArrowDown':
        e.target.blur();
        this.contentView.initCategoryNavigation();
        break;
      default:
        break;
    }
  }

  onSearchInputBlur(event){
    this.inputElement = event.target;
  }

  setSelectedCategory(selectedCategory) {
    this.setState({selectedCategory});
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data !== this.props.data || nextProps.readingLists !== this.props.readingLists) {
      this.setState({
        items: concat([], nextProps.data, nextProps.readingLists).map(
          (item) => assign({count: 0}, item)
        )
      });
    }
  }

  get
  selectedItems() {
    return Object.values(this.state.selectedItems).reduce(
      (agg, item) => {
        // TODO: Refactor to less-hacky grouping of selectedItems
        let { type: key } = item;
        const { id, count } = item;

        if(key === 'custom') {
          key += id;
        }
        agg[key] = {
          count,
          item
        };
        return agg;
      }, {});
  }

  increment(item) {
    // Custom widgets are always limited to 1, due to how we manage multiples of the same widget in a dashboard
    if (item.type === 'custom' && this.props.widgetIdsOnDashboard.includes(item.id)) {
      return;
    }
    // TODO: Avoid mutation of `item`? @see: https://facebook.github.io/react/docs/update.html
    item.count += 1;

    const { selectedItems } = this.state;
    const key = getWidgetKey(item);
    const newSelectedItems = assign({}, selectedItems, {
      [key]: item
    });

    this.setState({
      selectedItems: newSelectedItems
    });
  }

  decrement(item) {
    // TODO: Avoid mutation of `item`? @see: https://facebook.github.io/react/docs/update.html
    item.count = Math.max(0, item.count - 1);

    const { selectedItems } = this.state;
    let newSelectedItems = assign({}, selectedItems);
    if (item.count === 0) {
      const key = getWidgetKey(item);
      newSelectedItems = omit(newSelectedItems, key);
    }

    this.setState({
      selectedItems: newSelectedItems
    });
  }

  handleCancel() {
    this.setState(this.createInitialState());
    this.props.onHide();
  }

  handleKeyPress(e) {
    if (this.props.isOpen) {
      switch (e.key) {
        case 'Enter':
          this.handleSuccess();
          break;
        case 'Escape':
          this.handleCancel();
          break;
        default:
          break;
      }
    }
  }

  handleSuccess() {
    if (isEmpty(this.state.selectedItems)) {
      return;
    }

    this.props.onSuccess(this.selectedItems);
    this.setState(this.createInitialState());
  }

  handleItemSelection(item) {
    if (item.count === 0) {
      this.increment(item);
    }
  }

  toggleDrawer() {
    this.setState({isDrawerOpen: !this.state.isDrawerOpen});
  }

  addNewWidget(item) {
    const newItem = assign({ count: 0 }, item);

    this.props.onSaveCustomWidget(newItem, (newId) => {
      newItem.id = newId;
      this.setState({
        items: [...this.state.items, newItem]
      });
    });
  }

  editWidget(modifiedItem) {
    // Only custom widgets can be edited, we can assume the "key" is always the id
    const { items, selectedItems } = this.state;

    const index = items.indexOf(items.find(({id}) => id === modifiedItem.id));
    const itemsCollection = [ ...items ];

    if (index !== -1) {
      itemsCollection[index] = modifiedItem;
    }

    // Check if the edited item was in the list of items to add
    // if it was, update it there as well
    const newSelectedItems = { ...selectedItems };
    Object.keys(selectedItems).some((key) => {
      if (key === modifiedItem.id) {
        newSelectedItems[key] = modifiedItem;
        return true;
      }
      return false;
    });

    this.props.onSaveCustomWidget(modifiedItem, () => {
      // Update AddWidgetModal's widget collection and Drawer of selected items.
      this.setState({
        items: itemsCollection,
        selectedItems: newSelectedItems
      });
    });
  }

  deleteCustomWidget(itemToDelete) {
    const { id } = itemToDelete;
    const { items, selectedItems } = this.state;

    const index = items.indexOf(itemToDelete);
    const leftItems = items.slice(0, index);
    const rightItems = items.slice(index + 1);

    // Delete from AddWidgetModal's widget collection and from selected items Drawer.
    const key = getWidgetKey(itemToDelete);
    this.setState({
      items: [...leftItems, ...rightItems],
      selectedItems: omit(selectedItems, key)
    });

    this.props.deleteCustomWidget(id); // Delete from app
  }

  focusSearchInput() {
    if (this.inputElement){
      this.inputElement.focus();
    }
  }

  createInitialState() {
    const { data, readingLists } = this.props;
    return {
      isDrawerOpen: false,
      items: concat([], data, readingLists).map((item) => assign({count: 0}, item)),
      selectedCategory: 'Popular',
      selectedItems: {},
      searchText: '',
    };
  }

  render() {
    const {
      readingLists,
      fileUploadUrl,
      widgetIdsOnDashboard
    } = this.props;
    const {
      items,
      selectedItems,
      selectedCategory,
      searchText,
      isDrawerOpen
    } = this.state;

    const categoryNames =
      uniq(
        flatten(
          reject(items, (item) => includes(item.categories, 'custom'))
            .map((item) => item.categories)
        )
      ).sort().filter((categoryName) =>
        typeof categoryName === 'string' && categoryName !== ''
      );

    const groupedItems = categoryNames.map((category) => {
      return {
        name: category,
        items: sortBy(
          items.filter(
            (item) => item.categories && includes(item.categories, category)
          ), 'name')
      };
    });

    let hasSearchResults = false;
    if (searchText.length > 0) {
      const searchTerms = searchText.split(' ');
      const filteredItems = searchTerms.reduce(
        (results, term) => results.filter((result) => {
          const name = result.name || result.title || '';
          return typeof name === 'string' && includes(name.toLowerCase(), term.toLowerCase());
        }),
        items);

      hasSearchResults = filteredItems.length > 0;
      groupedItems.push({name: 'Search Results', items: filteredItems});
    }

    const selectedGroupedItems = find(groupedItems, {name: selectedCategory});
    const selectedCategories = selectedGroupedItems
      ? [selectedGroupedItems]
      : reject(groupedItems, {name: 'Search Results'});
    const customWidgets = items.filter((item) => includes(item.categories, 'custom'));
    // Reading Lists are also a mobile widget,
    // concat them to the list as such,
    // and sort them by title.
    const mobileWidgets = sortBy(
      concat(
        items.filter((item) => includes(item.categories, 'Mobile')),
        readingLists
      ),
      'title'
    );

    let contentView;
    if (selectedCategory === 'Search Results' && !hasSearchResults) {
      contentView = <EmptySearchResultsView>No search results found.</EmptySearchResultsView>;
    } else if (selectedCategory === 'Reading Lists') {
      contentView = (
        <CategoryView
          key="ReadingListsCategoryView"
          categories={[{
            name: 'Reading Lists',
            items: readingLists
          }]}
          increment={this.increment}
          decrement={this.decrement}
          onSelection={this.handleItemSelection}
          ref={(categoryContentView) => {this.contentView = categoryContentView;}}
          handleSuccess={this.handleSuccess}
          focusSearchInput={this.focusSearchInput}
        />
      );
    } else if(selectedCategory === 'Custom') {
      contentView = (
        <CustomWidgetCategoryView
          widgets={customWidgets}
          increment={this.increment}
          decrement={this.decrement}
          addNewWidget={this.addNewWidget}
          editWidget={this.editWidget}
          fileUploadUrl={fileUploadUrl}
          deleteCustomWidget={this.deleteCustomWidget}
          widgetIdsOnDashboard={widgetIdsOnDashboard}
          ref={(customContentView) => {this.contentView = customContentView;}}
          handleSuccess={this.handleSuccess}
          focusSearchInput={this.focusSearchInput}
        />
      );
    } else if (selectedCategory === 'Mobile') {
      contentView = (
        <CategoryView
          key="MobileCategoryView"
          categories={[{
            name: 'Mobile',
            items: mobileWidgets
          }]}
          increment={this.increment}
          decrement={this.decrement}
          onSelection={this.handleItemSelection}
        />
      );
    } else {
      contentView = (
        <CategoryView
          categories={selectedCategories}
          increment={this.increment}
          decrement={this.decrement}
          onSelection={this.handleItemSelection}
          ref={(categoryContentView) => {this.contentView = categoryContentView;}}
          handleSuccess={this.handleSuccess}
          focusSearchInput={this.focusSearchInput}
        />
      );
    }
    let drawerItems = null;
    if (!isEmpty(selectedItems)) {
      drawerItems = map(
        sortBy(values(selectedItems), 'name'), (item) => {
          const key = getWidgetKey(item);
          return (
            <li key={key}>
              <CategoryItem
                key={key}
                showDescription={false}
                increment={() => this.increment(item)}
                decrement={() => this.decrement(item)}
                item={item}
                showCount
              />
            </li>
          );
        }
      );
    }

    const drawerHeader = (
      <div>
        <span className={styles.summary}>Selected Widgets</span>
        <span className={styles.total}>
          {reduce(values(selectedItems), (sum, item) => sum + item.count, 0)}
        </span>
      </div>
    );

    const disableAddWidgetButton = isEmpty(selectedItems);

    return (
      <div onKeyDown={this.handleKeyPress} className={styles.base}>
        <Modal {...this.props} size="large" onBackdropClick={this.handleCancel} onEscapePress={this.handleCancel}>
          <div style={{backgroundColor: gray01, overflow: 'auto'}}>
            <ModalHeader onClose={this.handleCancel}>
              <Col xs={3}>
                <div className={styles.modalTitle}>Add Widgets</div>
              </Col>
              <Col xs={5}>
                <div className={styles.searchInput}>
                  <SearchInput
                    autoFocus
                    tabIndex="0"
                    onChange={this.onSearchInputChange}
                    onBlur={this.onSearchInputBlur}
                    onKeyDown={this.onSearchInputKeyDown}
                  />
                </div>
              </Col>
            </ModalHeader>

            <ModalBody>
              <Col xs={3}>
                <CategoryList
                  widgetCategories={map(groupedItems, 'name')}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={this.setSelectedCategory}
                />
              </Col>
              <Col xs={9}>
                <CategoryScrollView>
                  {contentView}
                </CategoryScrollView>

                <Drawer onClick={this.toggleDrawer} isOpen={isDrawerOpen} header={drawerHeader}>
                  {drawerItems}
                </Drawer>
              </Col>
            </ModalBody>

            <Footer>
              <div className={styles.buttonGroup}>
                <ButtonGroup align="right">
                  <Link linkType="cancel" onClick={this.handleCancel}>Cancel</Link>
                  <Button
                    tabIndex="0"
                    buttonStyle="positive"
                    onClick={this.handleSuccess}
                    disabled={disableAddWidgetButton}
                  >
                    Add Widgets
                  </Button>
                </ButtonGroup>
              </div>
            </Footer>
          </div>
        </Modal>
      </div>
    );
  }
}

AddWidgetModal.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  readingLists: PropTypes.arrayOf(PropTypes.object),
  deleteCustomWidget: PropTypes.func,
  fileUploadUrl: PropTypes.string,
  onHide: PropTypes.func,
  onSaveCustomWidget: PropTypes.func,
  onSuccess: PropTypes.func,
  isOpen: PropTypes.bool,
  widgetIdsOnDashboard: PropTypes.arrayOf(PropTypes.string)
};

AddWidgetModal.defaultProps = {
  data: [],
  readingLists: [],
  isOpen: false
};

export default AddWidgetModal;
